import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Loader2, AlertTriangle } from "lucide-react";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { refreshUser } = useAuth();
  const { t } = useLang();
  const [state, setState] = useState("checking"); // checking | paid | error
  const tries = useRef(0);

  useEffect(() => {
    let timer;
    const poll = async () => {
      if (!sessionId) { setState("error"); return; }
      if (tries.current >= 8) { setState("error"); return; }
      tries.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          await refreshUser();
          setState("paid");
          return;
        }
        if (["failed", "expired"].includes(data.payment_status)) {
          setState("error");
          return;
        }
      } catch { /* retry */ }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId, refreshUser]);

  return (
    <div className="min-h-screen bg-base text-white">
      <Navbar />
      <div className="max-w-md mx-auto px-4 py-24 text-center" data-testid="payment-success-page">
        {state === "checking" && (
          <>
            <Loader2 className="w-14 h-14 text-signal mx-auto mb-6 animate-spin" />
            <h1 className="font-heading font-black text-2xl mb-2">{t("payment_checking")}</h1>
          </>
        )}
        {state === "paid" && (
          <>
            <CheckCircle2 className="w-16 h-16 text-profit mx-auto mb-6" />
            <h1 data-testid="payment-success-title" className="font-heading font-black text-3xl mb-2">{t("payment_success_title")}</h1>
            <p className="font-mono text-sm text-white/50 mb-8">{t("payment_success_desc")}</p>
          </>
        )}
        {state === "error" && (
          <>
            <AlertTriangle className="w-16 h-16 text-loss mx-auto mb-6" />
            <h1 className="font-heading font-black text-2xl mb-8">{t("payment_cancelled_title")}</h1>
          </>
        )}
        <Link to="/profile">
          <Button data-testid="back-to-profile-btn" className="bg-signal text-black hover:bg-signal/90 rounded-sm font-semibold">
            {t("back_profile")}
          </Button>
        </Link>
      </div>
    </div>
  );
}
